import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { regionData, marketLabel, marketIcon } from "./markets";

/**
 * Compact market selector for the composer footer.
 *
 * The old selector was a row of five pills plus a country dropdown plus a
 * "Market: x" caption, all stacked above the input — roughly 140px of chrome
 * before you could type. The market matters, but it is a setting, not the
 * main event, so it collapses to a single button that opens a panel.
 */
export default function MarketPicker({
  selectedRegion,
  selectedCountry,
  onRegionChange,
  onCountryChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  const region = regionData[selectedRegion] || regionData.global;
  const Icon = marketIcon(selectedRegion, selectedCountry);
  const label = marketLabel(selectedRegion, selectedCountry);

  // Close on outside click or Escape, and hand focus back to the trigger so
  // keyboard users are not dropped at the top of the document.
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleRegionSelect = (regionId) => {
    onRegionChange?.(regionId);
    const countries = regionData[regionId]?.countries || [];
    if (countries.length === 1) {
      onCountryChange?.(countries[0].id);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-medium text-iq-ink-soft transition-colors hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand disabled:opacity-50"
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
        <FiChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose market"
          className="absolute bottom-full left-0 z-30 mb-2 w-64 rounded-xl border border-iq-line bg-white p-2 shadow-lg"
        >
          <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-iq-ink-faint">
            Region
          </p>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(regionData).map(([regionId, entry]) => {
              const RegionIcon = entry.icon;
              const active = selectedRegion === regionId;
              return (
                <button
                  key={regionId}
                  type="button"
                  onClick={() => handleRegionSelect(regionId)}
                  className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand ${
                    active
                      ? "bg-iq-brand-soft font-medium text-iq-brand"
                      : "text-iq-ink-soft hover:bg-iq-rail hover:text-iq-ink"
                  }`}
                >
                  <RegionIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{entry.shortName || entry.name}</span>
                </button>
              );
            })}
          </div>

          {region.countries.length > 1 && (
            <>
              <p className="px-2 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-iq-ink-faint">
                Country
              </p>
              <div className="max-h-48 overflow-y-auto">
                {region.countries.map((country) => {
                  const CountryIcon = country.icon;
                  const active = selectedCountry === country.id;
                  return (
                    <button
                      key={country.id}
                      type="button"
                      onClick={() => {
                        onCountryChange?.(country.id);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand ${
                        active
                          ? "bg-iq-brand-soft font-medium text-iq-brand"
                          : "text-iq-ink-soft hover:bg-iq-rail hover:text-iq-ink"
                      }`}
                    >
                      <CountryIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1 truncate">{country.name}</span>
                      {active && <FiCheck className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
